import { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  Card,
  Container,
  Skeleton,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RightSidebar from "../sidebar/SideBar";
import { Link, useNavigate } from "react-router-dom";
import CustomPagination from "../../components/custom-pagination/CustomPagination";
import Slider from "react-slick";
import { NewsStore } from "../../store/newsStore";
import dayjs from "dayjs";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BASE_URL } from "../../api/api";
import Preloader from "../../components/preloader/PreLoader";

const noImage = "/frontend/assets/logos/logo.png";
const backendLogoPattern = /\/logo\.(png|jpe?g|webp|gif)$/i;

const getFullImageUrl = (path) => {
  if (!path) return "";
  let url = path.startsWith("http")
    ? path
    : `${BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

  if (url.includes("ngrok") && !url.includes("ngrok-skip-browser-warning")) {
    url += (url.includes("?") ? "&" : "?") + "ngrok-skip-browser-warning=true";
  }
  return url;
};

const isBackendLogo = (path) => Boolean(path && backendLogoPattern.test(path.split("?")[0]));

const getNewsImageUrl = (item) => {
  const featuredImage =
    item?.all_images?.find((image) => image && !isBackendLogo(image)) ||
    (item?.image && !isBackendLogo(item.image) ? item.image : "");

  const sliderImage = item?.slider_images?.find(Boolean);

  return getFullImageUrl(featuredImage || sliderImage) || noImage;
};

const LazyImage = ({ src, alt, sx, className, hideOnError }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      if (imgRef.current.naturalWidth === 0) setError(true);
      else setLoaded(true);
    }
  }, [src]);

  if (error && hideOnError) return null;

  const displaySrc = error ? noImage : src;

  return (
    <Box sx={{ position: "relative", flexShrink: 0, ...sx }}>
      {!loaded && !error && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{ position: "absolute", top: 0, left: 0, zIndex: 1, borderRadius: sx?.borderRadius }}
        />
      )}
      <Box
        component="img"
        ref={imgRef}
        className={className}
        src={displaySrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: displaySrc === noImage ? "contain" : (sx?.objectFit || "cover"),
          objectPosition: sx?.objectPosition || "center center",
          borderRadius: sx?.borderRadius,
          opacity: loaded || error ? 1 : 0,
          transition: "opacity 0.4s ease-in-out, transform 0.3s ease-in-out",
          bgcolor: displaySrc === noImage ? "rgba(0,0,0,0.05)" : "transparent",
        }}
      />
    </Box>
  );
};

export default function NewsArticle() {
  const { fetchNews, news, totalPages, banners, fetchBanners } = NewsStore();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [adOpen, setAdOpen] = useState(false);
  const popupSliderRef = useRef(null);

  const popupSliderSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 4500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    pauseOnHover: true,
  };

  useEffect(() => {
    if (typeof fetchBanners === "function") {
      fetchBanners();
    }
  }, [fetchBanners]);

  const activeBannersList = (banners && banners.length > 0) ? banners : (news || []);

  const adBanners = activeBannersList
    .map((item) => {
      if (!item) return "";
      if (typeof item === "string") return getFullImageUrl(item);
      if (item.image) return getFullImageUrl(item.image);
      if (item.banner) return getFullImageUrl(item.banner);
      if (item.url) return getFullImageUrl(item.url);
      return getNewsImageUrl(item);
    })
    .filter((url) => url && url !== noImage);

  // Delay opening popup modal after page load (only 1st time per session)
  useEffect(() => {
    const hasShownAd = sessionStorage.getItem("janpath_ad_shown");
    if (!hasShownAd) {
      const openTimer = setTimeout(() => {
        setAdOpen(true);
        sessionStorage.setItem("janpath_ad_shown", "true");
      }, 3000);
      return () => clearTimeout(openTimer);
    }
  }, []);

  const handlePrevAd = () => {
    popupSliderRef.current?.slickPrev();
  };

  const handleNextAd = () => {
    popupSliderRef.current?.slickNext();
  };

  const handleCloseAd = () => {
    setAdOpen(false);
  };
  const truncateHtmlText = (html, maxLength = 150) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const getInitialSlides = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth < 600) return 1;
      if (window.innerWidth < 900) return 2;
    }
    return 4;
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 3500,
    arrows: false,
    pauseOnHover: false,
    pauseOnFocus: false,
    slidesToShow: getInitialSlides(),
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchNews(currentPage, ITEMS_PER_PAGE);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentPage]);

  if (loading) return <Preloader />;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          mt: { xs: 20, sm: 16, md: 20, lg: 22 },
          backgroundColor: "background.default",
          "& .slick-slider:not(.slick-initialized) .slick-slide:not(:first-of-type)": {
            display: "none",
          },
        }}
      >
        <Slider {...settings}>
          {news?.map((item) => (
            <div key={item.id}>
              <Box
                sx={{
                  height: { xs: 220, sm: 260, md: 300, lg: 360, xl: 400 },
                  px: { xs: 1.5, sm: 0.5 },
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover .slider-image": {
                      transform: "scale(1.05)",
                    },
                    "&:hover .slider-title": {
                      color: "#ff184e",
                    },
                  }}
                  onClick={() => navigate(`/${item?.slug}`)}
                >
                  <LazyImage
                    className="slider-image"
                    src={getNewsImageUrl(item)}
                    alt={item.title}
                    hideOnError={false}
                    sx={{ width: "100%", height: "100%" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)",
                      zIndex: 1,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: { xs: 8, sm: 12, md: 16 },
                      left: { xs: 8, sm: 12, md: 16 },
                      bgcolor: "#ff184e",
                      color: "#fff",
                      px: { xs: 1, sm: 1.5, md: 2 },
                      py: { xs: 0.5, sm: 1, md: 1 },
                      borderRadius: "4px",
                      fontSize: { xs: "10px", sm: "12px", md: "14px" },
                      fontWeight: 700,
                      textTransform: "uppercase",
                      zIndex: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    {item.state || item.category?.name}
                  </Box>
                  <Typography
                    className="slider-title"
                    sx={{
                      position: "absolute",
                      bottom: { xs: 10, sm: 20, md: 30 },
                      left: { xs: 10, sm: 20, md: 30 },
                      right: { xs: 10, sm: 20, md: 30 },
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: {
                        xs: "12px",
                        sm: "14px",
                        md: "16px",
                        lg: "14px",
                      },
                      zIndex: 2,
                      display: "-webkit-box",
                      overflow: "hidden",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      lineHeight: { xs: 1.4, sm: 1.3 },
                      transition: "color 0.3s ease-in-out",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              </Box>
            </div>
          ))}
        </Slider>
      </Box>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
          pb: 10,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 3, md: 4 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1fr) 300px",
              },
              gap: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Box
              component="section"
              sx={{
                order: { xs: 1, md: 1 },
                display: "flex",
                flexDirection: "column",
                gap: { xs: 3, sm: 4 },
              }}
            >
              {news.map((item) => (
                <Link
                  key={item.id}
                  to={`/${item?.slug}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Card
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "11px", sm: "12px" },
                        fontWeight: 700,
                        color: "#ff184e",
                        textTransform: "uppercase",
                        mb: 1.5,
                      }}
                    >
                      {item.category?.name}
                    </Typography>

                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "20px", sm: "24px", md: "28px" },
                        lineHeight: 1.3,
                        mb: 2,
                        cursor: "pointer",
                        color: "text.primary",
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Avatar
                        sx={{ width: 40, height: 40 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (item.author?.id)
                            navigate(`/author/${item.author.id}`);
                        }}
                        src={
                          item.author?.profile_image
                            ? item.author.profile_image.startsWith("http")
                              ? item.author.profile_image
                              : `${BASE_URL.replace(
                                /\/$/,
                                ""
                              )}/${item.author.profile_image.replace(
                                /^\//,
                                ""
                              )}`
                            : noImage
                        }
                      />
                      <Box>
                        <Typography
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: "14px", sm: "16px" },
                            transition: "0.3s",
                            cursor: "pointer",
                            "&:hover": { color: "#ff184e" },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (item.author?.id)
                              navigate(`/author/${item.author.id}`);
                          }}
                        >
                          {item.author?.name}
                        </Typography>
                        <Typography
                          color="text.secondary"
                          fontSize={{ xs: "11px", sm: "12px" }}
                        >
                          {dayjs(item?.published_at).format(
                            "MMMM D, YYYY h:mm A"
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {getNewsImageUrl(item) !== noImage && (
                      <LazyImage
                        src={getNewsImageUrl(item)}
                        alt={item.title}
                        hideOnError={true}
                        sx={{
                          width: "100%",
                          height: { xs: 220, sm: 300, md: 380, lg: 440 },
                          maxHeight: "500px",
                          borderRadius: 2,
                          mb: 2,
                          objectPosition: "center center",
                        }}
                      />
                    )}

                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: { xs: "14px", sm: "16px" },
                        lineHeight: 1.7,
                      }}
                    >
                      {truncateHtmlText(
                        item.description,
                        getNewsImageUrl(item) !== noImage ? 150 : 400
                      )}
                    </Typography>
                  </Card>
                </Link>
              ))}
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </Box>
            <Box
              component="aside"
              sx={{
                order: { xs: 2, md: 2 },
                position: { md: "sticky" },
                top: { md: 80 },
                height: "fit-content",
              }}
            >
              <RightSidebar />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Ad Banner Popup Modal with Blur Effect */}
      <Dialog
        open={adOpen && adBanners.length > 0}
        onClose={handleCloseAd}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0, 0, 0, 0.65)",
            },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        {adBanners.length > 0 && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: { xs: "95vw", sm: "750px", md: "900px", lg: "1000px" },
              bgcolor: "#0f172a",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,255,255,0.12)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Modal Header Badge & Close Button */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1,
                bgcolor: "rgba(15, 23, 42, 0.9)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                zIndex: 2,
              }}
            >
              <Box
                component="img"
                src={process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/logo512.png` : "/logo512.png"}
                alt="Janpath News 24"
                onError={(e) => {
                  if (e.target.src !== "/logo512.png" && e.target.src !== "/frontend/logo512.png") {
                    e.target.src = "/logo512.png";
                  }
                }}
                sx={{
                  height: { xs: "32px", sm: "40px" },
                  maxWidth: "180px",
                  objectFit: "contain",
                }}
              />
              <IconButton
                onClick={handleCloseAd}
                sx={{
                  color: "#ffffff",
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  "&:hover": { bgcolor: "#ff184e", color: "#fff" },
                  p: 0.8,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Ad Banner Image Slider Display with Horizontal Slide Animation */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: "340px", sm: "480px", md: "560px", lg: "600px" },
                overflow: "hidden",
                bgcolor: "#000",
                "& .slick-slider": { height: "100%" },
                "& .slick-list": { height: "100%" },
                "& .slick-track": { height: "100%", display: "flex" },
                "& .slick-slide": { height: "100%", "& > div": { height: "100%" } },
                "& .slick-dots": {
                  bottom: "12px",
                  zIndex: 5,
                  "& li button:before": {
                    fontSize: "10px",
                    color: "#ffffff",
                    opacity: 0.5,
                  },
                  "& li.slick-active button:before": {
                    color: "#ff184e",
                    opacity: 1,
                  },
                },
              }}
            >
              <Slider ref={popupSliderRef} {...popupSliderSettings}>
                {adBanners.map((imgUrl, index) => {
                  const bannerItem = activeBannersList[index];
                  return (
                    <Box
                      key={index}
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex !important",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: bannerItem?.slug || bannerItem?.link ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (bannerItem?.slug) {
                          setAdOpen(false);
                          navigate(`/${bannerItem.slug}`);
                        } else if (bannerItem?.link) {
                          window.open(bannerItem.link, "_blank");
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={imgUrl}
                        alt={`Ad Banner ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          userSelect: "none",
                        }}
                      />
                    </Box>
                  );
                })}
              </Slider>

              {/* Prev Arrow */}
              {adBanners.length > 1 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevAd();
                  }}
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                    "&:hover": { bgcolor: "#ff184e" },
                    zIndex: 10,
                  }}
                  size="medium"
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}

              {/* Next Arrow */}
              {adBanners.length > 1 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextAd();
                  }}
                  sx={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                    "&:hover": { bgcolor: "#ff184e" },
                    zIndex: 10,
                  }}
                  size="medium"
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
}
